/**
 * TrustBadgesWidget - Compact community trust credential chips for an entity.
 * Renders 4 data points sourced from /api/entities/:id/trust-stats.
 * @atomic @idempotent
 */
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldCheckIcon } from '@heroicons/react/24/outline'
import { createLogger } from '../../utils/logger'

const logger = createLogger('TrustBadgesWidget')

function formatShortDate(isoStr, lng) {
  if (!isoStr) return null
  try {
    const d = new Date(isoStr)
    return d.toLocaleDateString(lng || 'en', { year: 'numeric', month: 'short' })
  } catch (e) {
    logger.warn('Invalid date string', { isoStr })
    return null
  }
}

const Chip = memo(function Chip({ label, highlight = false }) {
  return (
    <span
      className={[
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
        highlight
          ? 'bg-primary/10 text-primary border border-primary/20'
          : 'bg-gray-100 text-gray-600 border border-gray-200',
      ].join(' ')}
    >
      {label}
    </span>
  )
})

const TrustBadgesWidget = memo(function TrustBadgesWidget({
  totalSightings,
  confirmationRate,
  activeSince,
  spottersToday,
}) {
  const { t, i18n } = useTranslation()

  if (!totalSightings && !activeSince) return null

  const confirmPct = confirmationRate != null
    ? Math.round(confirmationRate * 100)
    : null

  const activeSinceLabel = formatShortDate(activeSince, i18n.language)

  return (
    <div className="flex flex-wrap gap-1.5 items-center mt-1">
      <ShieldCheckIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
      {totalSightings > 0 && (
        <Chip
          label={t('entities.entity.trustBadges.sightings', '{{count}} sightings', { count: totalSightings })}
          highlight={totalSightings >= 10}
        />
      )}
      {confirmPct != null && confirmPct > 0 && (
        <Chip
          label={t('entities.entity.trustBadges.confirmationRate', '{{rate}}% confirmed', { rate: confirmPct })}
          highlight={confirmPct >= 50}
        />
      )}
      {activeSinceLabel && (
        <Chip
          label={t('entities.entity.trustBadges.activeSince', 'Active since {{date}}', { date: activeSinceLabel })}
        />
      )}
      {spottersToday > 0 && (
        <Chip
          label={t('entities.entity.trustBadges.spottersToday', '{{count}} spotter today', { count: spottersToday })}
          highlight
        />
      )}
    </div>
  )
})

export default TrustBadgesWidget
